// Turns the Planner add/edit form's fields into the task or event object that
// gets saved. Shared by the full form (ItemPanel in CalendarExtras.jsx) and the
// quick-add bar (QuickAddBar.jsx), so an item added either way has the same
// shape. Extracted unchanged from ItemPanel's save, 2026-10-09.
import { uid } from "./storage";
import { DEFAULT_TASK_COLOR, DEFAULT_EVENT_COLOR } from "../constants/palettes";
import { DEFAULT_ALARM_SOUND } from "../constants/misc";
import { intervalEveryOf, intervalUnitOf } from "./habitInterval";

export function buildItemPayload({
  isEdit = false, item = null, initialType, type, title, date, dueDate = "",
  // The due date's own time (tasks, Jade 2026-10-10). undefined = leave the
  // field as it is: an older task edited without picking a due time keeps having
  // no dueTime at all, so its reminders keep counting from the start time.
  dueTime = undefined,
  hasTime = false, time = "", endTime = "", categoryId = "", color = "",
  // The form's "All day" switch (2026-10-10). Events already store
  // allDay = !hasTime; a task stores it only when switched on, so the form can
  // show the switch on again when the task is edited.
  allDay = false,
  notes = "", subtasks = [], embeds = [], location = "", reminders = [],
  remindAnchor = "due", reminderType = "notification", alarmSound = DEFAULT_ALARM_SOUND,
  alarmSoundUri = "", alarmSoundLabel = "", attachments = [], priority = "",
  addToClockIn = true, freqType = "once", freqDays = [], monthDates = [], yearDates = [],
  yearlyScope = "annual", repeatEnd = "",
  // "Every N days / weeks / months" (freqType "interval", Jade 2026-10-16).
  intervalEvery = undefined, intervalUnit = undefined,
}) {
  // Only bake in the fixed default block color (pastel blue for tasks,
  // pastel purple for events) when there's no category to fall back on —
  // a category is picked, its color should win, same as event blocks
  // already do. A manual color pick always wins over both.
  const defaultColor = type === "event" ? DEFAULT_EVENT_COLOR : DEFAULT_TASK_COLOR;
  const finalColor = color || (categoryId ? undefined : defaultColor);
  const timeFields = hasTime ? { time, endTime } : { time: undefined, endTime: undefined };
  const freqFields = (type !== "task" && type !== "event") ? {} : (freqType === "once" ? {
    frequencyType: undefined, frequencyDays: undefined, monthDates: undefined, yearDates: undefined, yearlyOnce: undefined, repeatEnd: undefined,
    intervalEvery: undefined, intervalUnit: undefined,
  } : {
    frequencyType: freqType,
    frequencyDays: freqType === "specific" ? freqDays : undefined,
    monthDates:    freqType === "monthly"  ? monthDates : undefined,
    yearDates:     freqType === "yearly"   ? yearDates : undefined,
    intervalEvery: freqType === "interval" ? intervalEveryOf({ intervalUnit, intervalEvery }) : undefined,
    intervalUnit:  freqType === "interval" ? intervalUnitOf({ intervalUnit }) : undefined,
    // Yearly uses its own scope toggle instead of a repeat-until date.
    yearlyOnce:    freqType === "yearly"   ? (yearlyScope === "thisYear" || undefined) : undefined,
    repeatEnd:     freqType === "yearly"   ? undefined : (repeatEnd || undefined),
  });

  // When the user flips the type while editing, the item moves between the
  // tasks[] and events[] arrays. In that case we build a FRESH object of the
  // new shape (keeping the same id) instead of carrying over the old type's
  // fields (e.g. an event shouldn't keep a task's subtasks/done flags).
  const keepId = isEdit && item ? item.id : uid();
  const typeChanged = isEdit && type !== initialType;

  let payload;
  if (type === "event") {
    const base = (isEdit && item && !typeChanged) ? { ...item } : { id: keepId, description: "", repeat: "None", repeatDays: [], repeatDates: [] };
    payload = {
      ...base, id: keepId,
      title, date, ...timeFields, allDay: !hasTime,
      reminders, reminderType, alarmSound, alarmSoundUri, alarmSoundLabel, attachments, color: finalColor, categoryId,
      location, notes, embeds, ...freqFields, updatedAt: new Date().toISOString(),
    };
  } else {
    const base = (isEdit && item && !typeChanged) ? { ...item } : { id: keepId, done: false, subtasks: [], createdAt: (item && item.createdAt) || new Date().toISOString() };
    payload = {
      ...base, id: keepId,
      title, startDate: date, dueDate: dueDate || date, ...timeFields,
      allDay: (!hasTime && allDay) || undefined,
      notes, subtasks, embeds, goalId: categoryId || undefined, color: finalColor, ...freqFields,
      location, reminders, reminderType, alarmSound, alarmSoundUri, alarmSoundLabel, attachments, priority: priority || undefined, addToClockIn,
      // Only meaningful (and only saved) when start/due genuinely differ —
      // see _computeTaskOccurringOn in taskLogic.js and its server mirror in
      // lib/reminder-logic.js, which both branch on this to pick which date
      // a one-time task's reminders count down from.
      remindAnchor: (dueDate && freqType === "once") ? remindAnchor : undefined,
      // Only alongside a real one-time due date ("" = due that day, no time).
      ...(dueTime !== undefined ? { dueTime: (dueDate && freqType === "once") ? dueTime : undefined } : {}),
      // Manually re-checking Add-to-Clock-In clears any pending "just for
      // today" auto-restore marker (an unrelated edit that leaves it unchecked
      // keeps the temp removal intact).
      ...(addToClockIn ? { clockInRestoreDate: undefined } : {}),
    };
  }
  return payload;
}
